import { AuditTrailAction } from '../../audit/domain/auditTrailAction';
import { DashboardComponentType } from '../../dashboards/domain/dashboardComponentType';
import { LogSorterColumnPlacement } from '../../logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from '../../logSorter/domain/logSorterNumberSequence';
import { Anchor } from '../../map/domain/anchor';
import { ProductionOrderState } from '../../productionOrders/domain/productionOrderState';
import { ProductType } from '../../products/domain/productType';
import { ReportType } from '../../reports/domain/reportType';
import { ShapeMode } from '../../shapes/domain/shapeMode';
import { StorageAreaStatus } from '../../storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from '../../storageAreas/domain/storageAreaType';
import { VehicleType } from '../../vehicles/domain/vehicleType';
import { VehicleSymbol } from '../../vehicles/domain/vehicleSymbol';
import { KeyboardActions } from '../../vehicles/domain/keyboardActions';
import type { Translations } from '../domain/translations';
import { Unit } from '../../common/domain/bulkUnits';
import { DeliveryStatus } from '../../deliveries/domain/deliveryStatus';

export const german: Translations = {
  generic: {
    active: 'Aktiv',
    language: 'Sprache',
    yes: 'Ja',
    no: 'Nein',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    id: 'ID',
    name: 'Name',
    description: 'Beschreibung',
    type: 'Typ',
    code: 'Code',
    quality: 'Qualität',
    columns: 'Spalten',
    color: 'Farbe',
    capacity: 'Kapazität',
    loadingTableData: 'Wird geladen...',
    search: 'Suchen',
    generalErrorMsg: 'Hoppla! Ein Fehler ist aufgetreten',
    signIn: 'Anmelden',
    signOut: 'Abmelden',
    width: 'Breite',
    length: 'Länge',
    resetColumns: 'Spalten zurücksetzen',
    height: 'Höhe',
    login: 'Anmelden',
    logout: 'Abmelden',
    next: 'Weiter',
    back: 'Zurück',
    abort: 'Abbrechen',
    close: 'Schließen',
    searchBy: 'Suchen nach',
    clear: 'Löschen',
    error: 'Fehler',
    success: 'Erfolg',
    updated: 'Aktualisiert',
    added: 'Hinzugefügt',
    taskCompleted: 'Aufgabe abgeschlossen',
    created: 'Erstellt',
    deleted: 'Gelöscht',
    toggle: 'Umschalten',
    status: 'Status',
    position: 'Position',
    direction: 'Richtung',
    anchor: 'Anker',
    product: 'Produkt',
    vehicle: 'Fahrzeug',
    map: 'Karte',
    satellite: 'Satellit',
    settings: 'Einstellungen',
    geofence: 'Geofence',
    geofenceTop: 'Geofence oben',
    geofenceBottom: 'Geofence unten',
    geofenceLeft: 'Geofence links',
    geofenceRight: 'Geofence rechts',
    storageArea: 'Lagerbereich',
    inventory: 'Bestand',
    noAvailableOptions: 'Keine Optionen verfügbar',
    edit: 'Bearbeiten',
    select: 'Auswählen',
    default: 'Standard',
    mirrored: 'Gespiegelt',
    add: 'Hinzufügen',
    top: 'Oben',
    bottom: 'Unten',
    left: 'Links',
    right: 'Rechts',
    longitude: 'Längengrad',
    latitude: 'Breitengrad',
    rotation: 'Drehung',
    shape: 'Form',
    inside: 'Innen',
    outside: 'Außen',
    logSorter: 'Holzsortierer',
    confirm: 'Bestätigen',
    on: 'Ein',
    off: 'Aus',
    circle: 'Kreis',
    polygon: 'Polygon',
    size: 'Größe',
    rotate: 'Drehen',
    freeform: 'Freiform',
    rectangle: 'Rechteck',
    empty: 'Leer',
    discard: 'Verwerfen',
    audit: 'Prüfen',
    export: 'Exportieren',
    import: 'Importieren',
    take: 'Übernehmen',
    ok: 'OK',
    from: 'Von',
    to: 'Bis',
    move: 'Verschieben',
    coil: 'Aufwickeln',
    swap: 'Tauschen',
    copy: 'Kopieren',
    copyOfName: (name: string) => `${name} (Copy)`,
    pick: 'Auswählen',
    unknown: 'Unbekannt',
    enabled: 'Aktiviert',
    note: 'Notiz',
    groups: 'Gruppen',
    productGroup: 'Produktgruppe',
    products: 'Produkte',
    numberOfLogs: 'Anzahl der Stämme',
    balance: 'Saldo',
    fifo: 'FIFO',
    volume: 'Volumen',
    numberOfProductsInStorage: 'Anzahl der Produkte',
    storageAreas: 'Lagerbereiche',
    showMore: 'Mehr anzeigen',
    preview: 'Vorschau',
    selectColumns: 'Spalten auswählen',
    locked: 'Gesperrt',
    unlocked: 'Entsperrt',
    keybindings: 'Tastenkombinationen',
    productionOrder: 'Fertigungsauftrag',
    resource: 'Ressource',
    priority: 'Priorität',
    amount: 'Menge',
    newVersion: 'Neue Version von SILog',
    important: 'Wichtig',
    noData: 'Keine Daten',
    total: 'Gesamt',
    calculate: 'Berechnen',
    sorted: 'Sortiert',
    unsorted: 'Unsortiert',
    unit: 'Einheit',
    content: 'Inhalt',
    inTotal: 'insgesamt',
    reset: 'Zurücksetzen',
    resetToDefault: 'Auf Standard zurücksetzen',
    resetLayoutConfirmHeader: 'Layout zurücksetzen',
    resetLayoutConfirmMessage:
      'Das Layout des Dialogs auf die Standardanordnung zurücksetzen? Die Fenstergröße und -position bleiben erhalten.',
    customer: 'Kunde',
    customerReference: 'Kundenreferenz',
    user: 'Benutzer',
    weight: 'Gewicht',
    filter: 'Filtern',
  },
  application: {
    exit: 'Beenden',
    exitSilog: 'SILog beenden',
    confirmExit: 'Möchten Sie SILog wirklich beenden?',
    openSettings: 'App-Einstellungen öffnen',
  },
  sites: {
    actions: {
      fetchMany: {
        title: 'Standorte abrufen',
        error: 'Standorte konnten nicht abgerufen werden',
      },
    },
    notFound: 'Standort nicht gefunden',
  },
  sideMenu: {
    home: 'Startseite',
    map: 'Karte',
    vehicles: 'Fahrzeuge',
    products: 'Produkte',
    packages: 'Pakete',
    packaging: 'Verpackungen',
    packageTypes: 'Verpackungsarten',
    styles: 'Ausführungen',
    coils: 'Coils',
    systemsettings: 'Systemeinstellungen',
    dashboards: 'Dashboards',
    productionOrders: 'Fertigungsaufträge',
    reports: 'Berichte',
    deliveries: 'Lieferungen',
  },
  vehicleType(type: VehicleType): string {
    switch (type) {
      case VehicleType.Undefined:
        return 'Nicht definiert';
      case VehicleType.Automatic:
        return 'Automatisch';
      case VehicleType.LogHandler:
        return 'Holzstapler';
      case VehicleType.Truck:
        return 'LKW';
      case VehicleType.CoilForklift:
        return 'Coil-Gabelstapler';
      case VehicleType.BulkHandler:
        return 'Schüttgutstapler';
      case VehicleType.PackageHandler:
        return 'Paketstapler';
    }
  },
  vehicleSymbol(symbol: VehicleSymbol): string {
    switch (symbol) {
      case VehicleSymbol.Undefined:
        return 'Arrow (Standard)';
      case VehicleSymbol.LogStacker:
        return 'Stammstapler';
      case VehicleSymbol.WheelLoaderFrontGrapple:
        return 'Radlader – Frontgreifer';
      case VehicleSymbol.WheelLoaderHighGrapple:
        return 'Radlader – Hochgreifer';
      case VehicleSymbol.TimberCrane:
        return 'Holzkran';
      case VehicleSymbol.CoilForklift:
        return 'Coil-Gabelstapler';
    }
  },
  productType(type: ProductType): string {
    switch (type) {
      case ProductType.Log:
        return 'Stamm';
      case ProductType.Undefined:
        return 'Nicht definiert';
      case ProductType.Coil:
        return 'Coil';
      case ProductType.Bulk:
        return 'Schüttgut';
      case ProductType.Dimension:
        return 'Abmessung';
    }
  },
  products: {
    mapping: {
      matchCriteria: 'Übereinstimmungskriterien',
      target: 'Ziel',
      created: 'Erstellt',
      importRow: 'Importzeile',
      sourceProduct: 'Quellprodukt',
      sourceData: 'Quelldaten',
      result: 'Ergebnis',
      correction: 'Korrektur',
    },
    actions: {
      filterByProduct: 'Nach Produkt filtern',
      create: {
        title: 'Produkt anlegen',
        error: 'Produkt konnte nicht angelegt werden',
      },
      update: {
        title: 'Produkt aktualisieren',
        error: 'Produkt konnte nicht aktualisiert werden',
      },
      delete: {
        title: 'Produkt löschen',
        error: 'Produkt konnte nicht gelöscht werden',
        statusCodes: {
          409: 'Das Produkt wird im Lagerbereich noch verwendet',
        },
      },
      fetch: {
        title: 'Produkt abrufen',
        error: 'Produkt konnte nicht abgerufen werden',
      },
      fetchMany: {
        title: 'Produkte abrufen',
        error: 'Produkte konnten nicht abgerufen werden',
      },
      editLogYardProducts(number: number) {
        return `Edit ${number} logyard products`;
      },
      editCoilProducts(number: number) {
        return `Edit ${number} coil products`;
      },
      deleteMultipleProducts(number: number) {
        return `Delete ${number} products`;
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete product ${name}`;
      },
      description:
        'Möchten Sie dieses Produkt wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Produkt löschen',
    },

    products: 'Produkte',
    addNewProduct: 'Neues Produkt hinzufügen',
    editProduct: 'Produkt bearbeiten',
    editMultipleProducts(number: number) {
      return `${number} Produkte bearbeiten`;
    },
    fetchDataError: 'Produktdaten konnten nicht abgerufen werden',
    addNewProductError: 'Produkt konnte nicht angelegt werden',
    editProductError: 'Produkt konnte nicht aktualisiert werden',
    deleteProductError: 'Produkt konnte nicht gelöscht werden:',
    noProductsFound: 'Keine Produkte gefunden.',
    loadingProductData: 'Produktdaten werden geladen...',
    logsPerSquareMeter: 'Stämme/m²',
    notFound: 'Produkt nicht gefunden',
    couldNotFetchProduct: 'Produkt konnte nicht abgerufen werden!',
    logData: {
      logData: 'Stammdaten',
      logsPerSquareMeter: 'Stämme pro m²',
      minDiameter: 'Min. Durchmesser',
      maxDiameter: 'Max. Durchmesser',
      maxLength: 'Max. Länge',
      minLength: 'Min. Länge',
      averageCylinderVolume: 'Durchschnittliches Zylindervolumen',
      averagePhysicalVolume: 'Durchschnittliches physikalisches Volumen',
      isSorted: 'Ist sortiert',
      species: 'Holzart',
      logSorterName: 'Name des Stamsortierers',
      sawLineName: 'Name der Sägelinie',
      logsPerSquareMeterDescription:
        'Wird verwendet, um abzuschätzen, wie viele Stämme ein Fahrzeug aus einem Lagerbereich transportieren kann',
    },
    coilData: {
      title: 'Rolle',
      diameter: 'Durchmesser',
      depth: 'Tiefe',
    },
    bulkData: {
      title: 'Schüttvolumen',
      unit: 'Einheit',
      density: 'Dichte',
    },
    dimensionData: {
      title: 'Abmessungen',
      actualDimensions: 'Tatsächliche Abmessungen',
      nominalDimensions: 'Nennabmessungen',
      density: 'Dichte',
      thickness: 'Dicke',
      width: 'Breite',
      length: 'Länge',
      moisture: 'Feuchte',
    },
    importDialog: {
      title: 'CSV importieren',
      productType: 'Produkttyp',
      selectType: 'Typ auswählen',
      downloadTemplate: 'Importvorlage herunterladen',
      choose: 'Auswählen',
      upload: 'Hochladen',
      uploadedFilePrefix: 'Hochgeladene Datei:',
      selectTypeBeforeFile:
        'Bitte wählen Sie einen Produkttyp aus, bevor Sie eine Datei auswählen.',
      failedToParseCsvPrefix: 'CSV-Datei konnte nicht analysiert werden:',
      failedToParseCsvFile: 'Die CSV-Datei konnte nicht analysiert werden.',
      missingRequiredColumns: 'Es fehlen erforderliche Spalten:',
      invalidRowsOf: 'von',
      invalidRowsHaveErrors:
        'Produkten weisen Validierungsfehler auf. Bewegen Sie den Mauszeiger über das',
      hoverIconForCellDetails: 'Symbol in einer Zelle, um Details anzuzeigen.',
      allProductsValidPrefix: 'Alle',
      allProductsValidSuffix: 'Produkte scheinen gültig zu sein.',
      noDataLoaded: 'Es wurden noch keine Daten geladen. Wählen Sie oben eine CSV-Datei aus.',
      status: 'Status',
      importAborted: 'Import abgebrochen.',
      imported: 'Importierte',
      productsWord: 'Produkte.',
      summaryFailed: 'fehlgeschlagen – bewegen Sie den Mauszeiger über das',
      hoverIconForStatusDetails: 'Symbol in der Spalte „Status“, um Details anzuzeigen.',
      close: 'Schließen',
      import: 'Importieren',
      abort: 'Abbrechen',
      columnProductName: 'Produktname',
      columnDescription: 'Beschreibung',
      columnExternalCode: 'Externer Code',
      columnColor: 'Farbe',
      columnQuality: 'Qualität',
      columnSpecies: 'Baumart',
      columnLogsPerSquareMeter: 'Stämme/m²',
      columnMinDiameter: 'Min. Durchmesser',
      columnMaxDiameter: 'Max. Durchmesser',
      columnMaxLength: 'Max. Länge',
      columnMinLength: 'Min. Länge',
      columnAverageCylinderVolume: 'Durchschnittliches Zylindervolumen',
      columnAveragePhysicalVolume: 'Durchschnittliches physikalisches Volumen',
      columnSorted: 'Sortiert',
      columnLogSorter: 'Stammsortierer',
      columnSawLine: 'Sägelinie',
      columnDiameter: 'Durchmesser',
      columnDepth: 'Tiefe',
      columnDensity: 'Dichte',
      columnUnit: 'Einheit',
      validationRequired: 'ist erforderlich.',
      validationMustBeNumber: 'muss eine Zahl sein.',
      validationMustBeInteger: 'muss eine ganze Zahl sein.',
      validationMustBeBoolean: 'muss TRUE oder FALSE sein.',
      validationMustBeHexColor: 'muss eine Hex-Farbe sein (z. B. #4d8aeb).',
      validationMustBeOneOf: 'muss einer der folgenden Werte sein:',
      validationMustBeAtLeast: 'muss mindestens betragen:',
      duplicateProductNameInFile: 'Doppelte Produktbezeichnung in dieser Datei.',
      productNameAlreadyExists: 'Ein Produkt mit diesem Namen existiert bereits.',
      importFailedFallback: 'Import fehlgeschlagen.',
    },
    deleteMultipleDialog: {
      deleteProducts: 'Produkte löschen',
      areYouSure: 'Möchten Sie diese Produkte wirklich löschen?',
      thisActionCannotBeUndone: 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
      deleted: 'Gelöscht',
      products: 'Produkte',
      summaryFailed: 'Fehlgeschlagen – bewegen Sie den Mauszeiger über das',
      hoverIconForDetails: 'Symbol in der Spalte „Status“, um weitere Details anzuzeigen.',
      deleteAborted: 'Der Löschvorgang wurde abgebrochen.',
      type: 'Geben Sie',
      DELETE: 'DELETE',
      toConfirm: 'ein, um zu bestätigen',
      noProductsSelected: 'Keine Produkte ausgewählt',
      deleteFailed: 'Löschen fehlgeschlagen',
    },
  },
  productGroups: {
    actions: {
      create: {
        title: 'Produktgruppe erstellen',
        error: 'Erstellen der Produktgruppe fehlgeschlagen',
      },
      update: {
        title: 'Produktgruppe aktualisieren',
        error: 'Aktualisieren der Produktgruppe fehlgeschlagen',
      },
      delete: {
        title: 'Produktgruppe löschen',
        error: 'Löschen der Produktgruppe fehlgeschlagen',
      },
      fetch: {
        title: 'Produktgruppe abrufen',
        error: 'Das Abrufen der Produktgruppe ist fehlgeschlagen',
      },
      fetchMany: {
        title: 'Produktgruppen werden abgerufen',
        error: 'Das Abrufen der Produktgruppen ist fehlgeschlagen',
      },
    },
    listPage: {
      title: 'Produktgruppen',
      empty: 'Keine Produktgruppen',
      loading: 'Produktgruppen werden geladen...',
      addNew: 'Neue Gruppe hinzufügen',
      edit: 'Gruppe bearbeiten',
    },
    deleteDialog: {
      header(name: string) {
        return `Produktgruppe ${name} löschen`;
      },
      description:
        'Möchten Sie diese Produktgruppe wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Produktgruppe löschen',
    },
  },
  vehicles: {
    panels: {
      identity: 'Identität',
      preview: 'Vorschau',
      type: 'Typ',
      movement: 'Bewegung',
      automatic: 'Automatisch',
      keybindings: 'Tastenbelegungen',
    },
    actions: {
      create: {
        title: 'Fahrzeug wird erstellt',
        error: 'Das Erstellen des Fahrzeugs ist fehlgeschlagen',
      },
      update: {
        title: 'Fahrzeug wird aktualisiert',
        error: 'Aktualisierung des Fahrzeugs fehlgeschlagen',
      },
      delete: {
        title: 'Fahrzeug löschen',
        error: 'Löschen des Fahrzeugs fehlgeschlagen',
      },
      fetch: {
        title: 'Fahrzeug abrufen',
        error: 'Abrufen des Fahrzeugs fehlgeschlagen',
      },
      fetchMany: {
        title: 'Fahrzeuge abrufen',
        error: 'Abrufen der Fahrzeuge fehlgeschlagen',
      },
      startReservation: {
        title: 'Fahrzeugreservierung starten',
        error: 'Reservierung des Fahrzeugs fehlgeschlagen',
      },
      endReservation: {
        title: 'Fahrzeugreservierung beenden',
        error: 'Beenden der Fahrzeugreservierung fehlgeschlagen',
      },
      updateKeybinds: {
        title: 'Tastenbelegung des Fahrzeugs aktualisieren',
        error: 'Aktualisierung der Tastenbelegung des Fahrzeugs fehlgeschlagen',
      },
    },
    vehicles: 'Fahrzeuge',
    noVehiclesFound: 'Keine Fahrzeuge gefunden.',
    loadingVehicleData: 'Fahrzeugdaten werden geladen...',
    addNewVehicle: 'Neues Fahrzeug hinzufügen',
    editVehicle: 'Fahrzeug bearbeiten',
    confirmDeleteMsg:
      'Möchten Sie dieses Fahrzeug wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    deleteBtnTxt: 'Ja, Fahrzeug löschen',
    fetchDataError: 'Das Abrufen der Fahrzeugdaten ist fehlgeschlagen',
    editVehicleSuccess: 'Bearbeitet',
    addNewVehicleSuccess: 'Erfolgreich: hinzugefügt',
    deleteVehicleSuccess: 'Erfolgreich: gelöscht',
    deleteVehicleError: 'Fahrzeug konnte nicht gelöscht werden:',
    nameMustBeUnique: 'Der Name muss eindeutig sein',
    symbol: 'Symbol',
    symbolPreview: 'Vorschau',
    anchorPoint: 'Ankerpunkt',
    anchorRotationOffset: 'Rotationsversatz',
    anchorRotationOffsetDescription:
      'Im Uhrzeigersinn angewendete Drehung des Fahrzeugsymbols und der Annäherungsrichtung, relativ zur GPS-Kursrichtung. Für keinen Versatz auf 0 belassen.',
    usePickupPoint: 'Separater Abholpunkt',
    usePickupPointDescription:
      'Wenn diese Option aktiviert ist, beginnen die Näherungs- und Flächenprüfungen für Baumstämme oder Spulen an einem separaten Abholpunkt auf dem Symbol statt am Ankerpunkt.',
    pickupPoint: 'Abholpunkt',
    capacityDescription:
      'Wird verwendet, um abzuschätzen, wie viele Baumstämme dieses Fahrzeug aus einem Lagerbereich aufnehmen kann. Wird in Kombination mit den Produkten „Baumstämme pro Quadratmeter“ verwendet.',
    packageCapacity: 'Paketkapazität',
    packageCapacityDescription:
      'Maximale Anzahl an Paketen, die dieses Fahrzeug gleichzeitig transportieren kann.',
    proximityRange: 'Näherungsbereich',
    proximityRangeDescription: 'Erfassungsbereich für die Abholung und Abgabe von Paketen.',
    proximityConeAngle: 'Näherungskegel',
    proximityConeAngleDescription:
      'Halbwinkel des Zugangskegels für die Abholung und Abgabe von Paketen. Das Fahrzeug muss sich einem Ort aus diesem Winkel nähern.',
    headingSmoothingTime: 'Kursglättung',
    headingSmoothingTimeDescription:
      'Zeitkonstante zur Glättung des Fahrzeugkurses, um GPS-Schwankungen zu reduzieren. Höhere Werte sorgen für eine glattere Darstellung, aber eine langsamere Reaktion. Leer lassen, um keine Glättung zu verwenden (der Kurs folgt direkt dem GPS).',
    stationarySpeedThreshold: 'Schwellenwert für Stillstand',
    stationarySpeedThresholdDescription:
      'Unterhalb dieser Geschwindigkeit wird das Fahrzeug als stillstehend behandelt und seine Fahrtrichtung wird konstant gehalten, anstatt der unzuverlässigen GPS-Fahrtrichtung zu folgen. Leer lassen, um den Standardwert von 0,5 km/h beizubehalten. Hat keine Auswirkung, wenn „Neutral-Drehung“ aktiviert ist.',
    neutralTurn: 'Neutral-Drehung',
    neutralTurnDescription: 'Das Fahrzeug kann sich auf der Stelle drehen.',
    reverseDetection: 'Rückwärtserkennung',
    reverseDetectionDescription:
      'Erkennt, wenn das Fahrzeug rückwärts fährt, und passt die Richtung entsprechend an.',
    packagePickupDirection: 'Paket-Aufnahmerichtung',
    packagePickupDirectionDescription:
      'Von welcher Seite eines Pakets aus dieses Fahrzeug Pakete aufnimmt und abstellt.',
    packagePickupDirectionAny: 'Beliebige Seite',
    packagePickupDirectionLengthSide: 'Längsseite (links/rechts)',
    packagePickupDirectionWidthSide: 'Breite Seite (Enden)',
    placementPreview: 'Vorschau der Platzierung',
    placementPreviewDescription:
      'Zeigt ein halbtransparentes Schattenbild der geplanten Paketpositionen an, bevor diese abgestellt werden.',
    deleteVehicle(name: string) {
      return `Fahrzeug ${name} löschen`;
    },
    inUseDialog: {
      header: 'Fahrzeug ist in Gebrauch!',
    },
    reservationEnded: 'Jemand anderes hat Ihr Fahrzeug übernommen!|',
    reservationEndedDescription: (userId: string) =>
      `Ihr Fahrzeug wurde von ${userId.split('|')[0]} übernommen`,
    automaticLogYardMoves: 'Automatische Bewegungen im Holzlager',
    automaticLogYardMovesLogSorterBinFull: 'Wenn der Behälter des Holzsortierers voll ist',
    automaticLogYardMovesLogSorterBinChangeProduct:
      'Wenn der Behälter des Holzsortierers das Produkt wechselt',
  },
  addLogSorterDialog: {
    addLogSorter: 'Holzsortierer hinzufügen',
    editLogSorter: 'Holzsortierer bearbeiten',
    name: 'Name',
    bin: 'Behälter',
    bins: 'Behälter',
    identifiers: 'ID',
    logSorter: 'Holzsortierer',
    selectLogSorter: 'Holzsortierer auswählen',
    startPositionX: 'Startposition X',
    startPositionY: 'Startposition Y',
    columns: 'Spalten',
    numberOfBins: 'Anzahl der Behälter',
    numberSequence: 'Nummernfolge',
    rotation: 'Drehung',
    direction: 'Richtung',
    binWidth: 'Behälterbreite',
    binLength: 'Behälterhöhe',
    distanceX: 'Abstand zwischen X',
    distanceY: 'Abstand zwischen Y',
    geoFenceOffsetTop: 'Geofence oben',
    geoFenceOffsetBottom: 'Geofence unten',
    geoFenceOffsetLeft: 'Geofence links',
    geoFenceOffsetRight: 'Geofence rechts',
    geoFenceOffsetInside: 'Geofence innen',
    geoFenceOffsetOutside: 'Geofence außen',
    addLogWhenSorted: 'Stamm nach Sortierung hinzufügen',
    intake: 'Annahme',
    intakes: 'Annahmen',
    addNewIntake: 'Neue Annahme hinzufügen',
    deleteIntake: 'Annahme löschen',
    savingStorageAreas: 'Lagerbereiche speichern',
    savingShapes: 'Formen speichern',
    savingLogSorter: 'Holzsortierer speichern',
    success: 'Erfolgreich!',
    savingBins: 'Lagerplätze speichern',
    savingIntakes: 'Annahmen speichern',
    one: '1',
    twoLeft: '2L',
    twoRight: '2R',
  },
  dialogs: {
    storageArea: {
      addStorageArea: 'Lagerbereich anlegen',
      editStorageArea: 'Lagerbereich bearbeiten',
      storageArea: 'Lagerbereich',
      automaticProductChange: 'Automatischer Produktwechsel',
      allowMultipleProducts: 'Mehrere Produkte zulassen',
      allowedProducts: 'Zulässige Produkte',
      selectAllowedProducts: 'Zulässige Produkte auswählen',
      selectStorageAreaType: 'Lagerbereichstyp auswählen',
      selectStorageAreaStatus: 'Lagerbereichsstatus auswählen',
      confirmDeleteHeader: 'Lagerbereich löschen',
      confirmDeleteMessage: (name: string) =>
        `Sind Sie sicher, dass Sie den Lagerbereich ${name} löschen möchten?`,
      deleteError: 'Lagerbereich konnte nicht gelöscht werden',
      updateError: 'Lagerbereich konnte nicht aktualisiert werden',
      createError: 'Lagerbereich konnte nicht angelegt werden',
      editShape: 'Form bearbeiten',
      sideArea: 'Seitenbereich',
      sideAreaDescription:
        'Dient zur Schätzung der Menge an Holzstämmen, die in diesen Lagerbereich passt',
    },
    storageAreaSelect: {
      title: 'Informationen zum Lagerbereich',
    },
    inventory: {
      title: 'Bestand',
      selectProduct: 'Produkt auswählen',
      currentAmount: 'Aktuelle Menge:',
      setNewAmount: 'Neue Menge festlegen',
      storageAreaNotFound: 'Lagerbereich nicht gefunden!',
      confirmHeader: (name: string) => `Inventar ${name}`,
      confirmMessage: (name: string, product: string) =>
        `Sind Sie sicher, dass Sie ${product} zu ${name} hinzufügen möchten? Mehrere Produkte zulassen ist deaktiviert.`,
    },
    confirmEmpty: {
      message:
        'Der Lagerbereich enthält noch Inhalte. Möchten Sie ihn wirklich leeren? Die verbleibenden Inhalte werden gelöscht.',
      title: (name: string) => `Lagerbereich ${name} leeren`,
      willBeDeleted: 'werden gelöscht',
    },
    confirmAllowMultipleProducts: {
      message: (storageAreaName: string, product: string) =>
        `Mehrere Produkte zulassen ist deaktiviert. Sind Sie sicher, dass Sie "${product}" in ${storageAreaName} platzieren möchten?`,
      title: 'Warnung',
    },
    confirmPlaceNotAllowedProductInLogYard: {
      message: (storageAreaName: string, products: string) =>
        `Das Produkt "${products}" ist in ${storageAreaName} nicht zugelassen. Sind Sie sicher, dass Sie es hier platzieren möchten?`,
      title: 'Warnung',
    },
    moveLogYardContent: {
      pickTab: 'Auswählen aus',
      pickButton: 'Auswählen',
      placeTab: 'Ablegen in',
      placeButton: 'Ablegen',
      pickAll: 'Leeren',
    },
    selectVehicle: {
      title: 'Fahrzeug auswählen',
      message: 'Bitte wähle das Fahrzeug aus, das du verwendest',
    },
    vehicleInventory: {
      vehicleInventory: 'Fahrzeugbestand',
      vehicleLoad: 'Aktuelle Fahrzeugladung',
      leaveTitle: 'Dort belassen',
      leaveButton: 'Belassen',
      moving: 'Transport',
    },
    search: {
      searchForProduct: 'Nach einem Produkt suchen',
      selectAProduct: 'Produkt auswählen',
    },
    mapView: {
      title: 'Neue Kartenansicht hinzufügen',
      enterName: 'Name eingeben',
    },
    selectLogSorter: {
      title: 'Holzsortierer auswählen',
    },
    productionOrder: {
      editTitle: 'Produktionsauftrag auswählen',
      selectProductionOrder: 'Produktionsauftrag auswählen',
    },
  },
  error: {
    required: 'Erforderlich.',
    invalidValue: 'Ungültiger Wert.',
    nameAlreadyExist: 'Name bereits vorhanden.',
    nameIsRequired: 'Ein Name ist erforderlich.',
    maxLength: (limit: number) => `Max. Länge ${limit}`,
    forbidden: 'Nicht zulässig',
    notFound: 'Nicht gefunden',
    conflict: 'Konflikt',
    network: 'Server konnte nicht erreicht werden',
    invalidDensity: 'Die Dichte muss mindestens 0,1 betragen',
  },
  enums: {
    storageAreaStatus(status: StorageAreaStatus): string {
      switch (status) {
        case StorageAreaStatus.Empty:
          return 'Leer';
        case StorageAreaStatus.InUse:
          return 'In Gebrauch';
        case StorageAreaStatus.Full:
          return 'Voll';
        case StorageAreaStatus.Undefined:
          return 'Undefiniert';
      }
    },
    storageAreaType(type: StorageAreaType): string {
      switch (type) {
        case StorageAreaType.Producer:
          return 'Produzent';
        case StorageAreaType.Consumer:
          return 'Verbraucher';
        case StorageAreaType.Vehicle:
          return 'Fahrzeug';
        case StorageAreaType.LogSorterIntake:
          return 'Aufnahme des Holzsortierers';
        case StorageAreaType.SawLineIntake:
          return 'Aufnahme der Sägelinie';
        case StorageAreaType.LogSorterBin:
          return 'Behälter des Holzsortierers';
        case StorageAreaType.SortedPile:
          return 'Sortierter Stapel';
        case StorageAreaType.UnsortedPile:
          return 'Unsortierter Stapel';
        case StorageAreaType.Truck:
          return 'LKW';
        case StorageAreaType.Train:
          return 'Zug';
        case StorageAreaType.Undefined:
          return 'Undefiniert';
        case StorageAreaType.CoilStack:
          return 'Coil-Stapel';
        case StorageAreaType.Bulk:
          return 'Schüttgut';
        case StorageAreaType.PackageStorage:
          return 'Paketlager';
        case StorageAreaType.ProductionLine:
          return 'Produktionslinie';
        case StorageAreaType.PackageOutput:
          return 'Paketausgang';
        case StorageAreaType.PackageIntake:
          return 'Paketeingang';
      }
    },
    shapeMode(type: ShapeMode): string {
      switch (type) {
        case ShapeMode.Rectangle:
          return 'Rechteck';
        case ShapeMode.Circle:
          return 'Kreis';
        case ShapeMode.Polygon:
          return 'Polygon';
      }
    },
    logSorterNumberSequence(value: LogSorterNumberSequence): string {
      switch (value) {
        case LogSorterNumberSequence.Sequential:
          return 'Sequenziell';
        case LogSorterNumberSequence.SequentialMirrored:
          return 'Sequenziell gespiegelt';
        case LogSorterNumberSequence.Alternating:
          return 'Abwechselnd';
        case LogSorterNumberSequence.AlternatingMirrored:
          return 'Abwechselnd gespiegelt';
      }
    },
    logSorterBinLayout(value: LogSorterColumnPlacement): string {
      switch (value) {
        case LogSorterColumnPlacement.Left:
          return 'Links';
        case LogSorterColumnPlacement.Right:
          return 'Rechts';
      }
    },
    anchor(value: Anchor): string {
      switch (value) {
        case Anchor.Center:
          return 'Mitte';
        case Anchor.BottomCenter:
          return 'Unten Mitte';
        case Anchor.BottomLeft:
          return 'Unten Links';
        case Anchor.BottomRight:
          return 'Unten Rechts';
        case Anchor.TopCenter:
          return 'Oben Mitte';
        case Anchor.TopLeft:
          return 'Oben Links';
        case Anchor.TopRight:
          return 'Oben Rechts';
      }
    },
    auditTrailAction(action: AuditTrailAction): string {
      switch (action) {
        case AuditTrailAction.InventoryApiExecute:
          return 'Bestand';
        case AuditTrailAction.InventoryApiUndo:
          return 'Bestand rückgängig machen';
        case AuditTrailAction.LogSorterApiCreate:
          return 'Holzsortierer erstellen';
        case AuditTrailAction.LogSorterApiDelete:
          return 'Holzsortierer löschen';
        case AuditTrailAction.LogSorterApiUpdate:
          return 'Holzsortierer aktualisieren';
        case AuditTrailAction.MapStyleApiCreate:
          return 'Kartenstil erstellen';
        case AuditTrailAction.MapStyleApiDelete:
          return 'Kartenstil löschen';
        case AuditTrailAction.MapStyleApiUpdate:
          return 'Kartenstil aktualisieren';
        case AuditTrailAction.MoveApiUndo:
          return 'Verschieben rückgängig machen';
        case AuditTrailAction.MoveApi:
          return 'Verschieben';
        case AuditTrailAction.ProductApiCreate:
          return 'Produkt erstellen';
        case AuditTrailAction.ProductApiDelete:
          return 'Produkt löschen';
        case AuditTrailAction.ProductApiUpdate:
          return 'Produkt aktualisieren';
        case AuditTrailAction.PackageApiCreate:
          return 'Paket erstellen';
        case AuditTrailAction.PackageApiUpdate:
          return 'Paket aktualisieren';
        case AuditTrailAction.PackageApiDelete:
          return 'Paket löschen';
        case AuditTrailAction.PackagingApiCreate:
          return 'Verpackung erstellen';
        case AuditTrailAction.PackagingApiUpdate:
          return 'Verpackung aktualisieren';
        case AuditTrailAction.PackagingApiDelete:
          return 'Verpackung löschen';
        case AuditTrailAction.PackageTypeApiCreate:
          return 'Pakettyp erstellen';
        case AuditTrailAction.PackageTypeApiUpdate:
          return 'Pakettyp aktualisieren';
        case AuditTrailAction.PackageTypeApiDelete:
          return 'Pakettyp löschen';
        case AuditTrailAction.SyncPackage:
          return 'Paket aktualisieren';
        case AuditTrailAction.ShapeApiCreate:
          return 'Form erstellen';
        case AuditTrailAction.ShapeApiDelete:
          return 'Form löschen';
        case AuditTrailAction.ShapeApiUpdate:
          return 'Form aktualisieren';
        case AuditTrailAction.ShapeApiUndoDelete:
          return 'Aktualisierung der Form rückgängig machen';
        case AuditTrailAction.StorageAreaApiCreate:
          return 'Lagerbereich erstellen';
        case AuditTrailAction.StorageAreaApiDelete:
          return 'Lagerbereich löschen';
        case AuditTrailAction.StorageAreaApiUndoDelete:
          return 'Löschen des Lagerbereichs rückgängig machen';
        case AuditTrailAction.StorageAreaApiUpdate:
          return 'Lagerbereich aktualisieren';
        case AuditTrailAction.SyncMove:
          return 'Verschieben';
        case AuditTrailAction.VehicleApiCreate:
          return 'Fahrzeug erstellen';
        case AuditTrailAction.VehicleApiDelete:
          return 'Fahrzeug löschen';
        case AuditTrailAction.VehicleApiUpdate:
          return 'Fahrzeug aktualisieren';
        case AuditTrailAction.Undefined:
          return 'Nicht definiert';
        case AuditTrailAction.SyncInventory:
          return 'Bestand synchronisieren';
        case AuditTrailAction.SyncProduct:
          return 'Produkt synchronisieren';
        case AuditTrailAction.SyncShape:
          return 'Form synchronisieren';
        case AuditTrailAction.SyncStorageArea:
          return 'Lagerbereich synchronisieren';
        case AuditTrailAction.SyncUndoInventory:
          return 'Inventar-Rückgängigmachung synchronisieren';
        case AuditTrailAction.SyncUndoMove:
          return 'Rückgängigmachen der Verschiebung synchronisieren';
        case AuditTrailAction.SyncUndoStorageArea:
          return 'Rückgängigmachen des Lagerbereichs synchronisieren';
        case AuditTrailAction.CoilApiCreate:
          return 'Coil erstellen';
        case AuditTrailAction.CoilApiUpdate:
          return 'Coil aktualisieren';
        case AuditTrailAction.CoilApiDelete:
          return 'Coil löschen';
        case AuditTrailAction.FeatureFlagApi:
          return 'Feature-Flags aktualisieren';
        case AuditTrailAction.SettingUpdate:
          return 'Einstellungen aktualisieren';
        case AuditTrailAction.SyncFeatureFlag:
          return 'Feature-Flags synchronisieren';
        case AuditTrailAction.SyncLogSorter:
          return 'Holzsortierer synchronisieren';
        case AuditTrailAction.SyncMapStyle:
          return 'Kartenstile synchronisieren';
        case AuditTrailAction.SyncSetting:
          return 'Einstellungen synchronisieren';
        case AuditTrailAction.SyncVehicle:
          return 'Fahrzeuge synchronisieren';
        case AuditTrailAction.ProductGroupApiCreate:
          return 'Produktgruppe erstellen';
        case AuditTrailAction.ProductGroupApiUpdate:
          return 'Produktgruppe aktualisieren';
        case AuditTrailAction.ProductGroupApiDelete:
          return 'Produktgruppe löschen';
        case AuditTrailAction.ProductGroupSync:
          return 'Produktgruppe synchronisieren';
        case AuditTrailAction.DashboardCreate:
          return 'Dashboard erstellen';
        case AuditTrailAction.DashboardUpdate:
          return 'Dashboard aktualisieren';
        case AuditTrailAction.DashboardDelete:
          return 'Dashboard löschen';
        case AuditTrailAction.MoveTimberFromLogSorterBins:
          return 'Holz aus den Behältern des Holzsortierers verschieben';
        case AuditTrailAction.ProductionOrderCreate:
          return 'Fertigungsauftrag anlegen';
        case AuditTrailAction.ProductionOrderUpdate:
          return 'Fertigungsauftrag aktualisieren';
        case AuditTrailAction.ProductionOrderDelete:
          return 'Fertigungsauftrag löschen';
        case AuditTrailAction.ProductionOrderSync:
          return 'Fertigungsauftrag synchronisieren';
        case AuditTrailAction.ProductionOrderChange:
          return 'Fertigungsauftrag ändern';
        case AuditTrailAction.InboundDeliveryCreate:
          return 'Wareneingang anlegen';
        case AuditTrailAction.InboundLoadCreate:
          return 'Wareneingangsladung anlegen';
        case AuditTrailAction.SyncInboundLoad:
          return 'Wareneingangsladung synchronisieren';
        case AuditTrailAction.SyncInboundDelivery:
          return 'Wareneingang synchronisieren';
        case AuditTrailAction.InboundDeliveryDelete:
          return 'Wareneingang löschen';
        case AuditTrailAction.InboundDeliveryUpdate:
          return 'Wareneingang aktualisieren';
        case AuditTrailAction.OutboundDeliveryCreate:
          return 'Ausgehende Lieferung erstellen';
        case AuditTrailAction.OutboundDeliveryUpdate:
          return 'Ausgehende Lieferung aktualisieren';
        case AuditTrailAction.OutboundDeliveryDelete:
          return 'Ausgehende Lieferung löschen';
        case AuditTrailAction.SyncOutboundDelivery:
          return 'Ausgehende Lieferung synchronisieren';
        case AuditTrailAction.InboundDeliveryTransaction:
          return 'Wareneingangstransaktion';
        case AuditTrailAction.OutboundDeliveryTransaction:
          return 'Warenausgangstransaktion';
        case AuditTrailAction.SyncInboundDeliveryTransaction:
          return 'Wareneingangstransaktion synchronisieren';
        case AuditTrailAction.SyncOutboundDeliveryTransaction:
          return 'Warenausgangstransaktion synchronisieren';
        default:
          return 'Unbekannte Aktion';
      }
    },
    dashboardComponentType(type: DashboardComponentType): string {
      switch (type) {
        case DashboardComponentType.StorageAreaTable:
          return 'Lagerbereichstabelle';
        case DashboardComponentType.LatestReleaseNotes:
          return 'Aktuelle Versionshinweise';
        case DashboardComponentType.ProductGroupDiagram:
          return 'Produktgruppendiagramm';
        case DashboardComponentType.ProductGroupTable:
          return 'Produktgruppentabelle';
        case DashboardComponentType.ProductTable:
          return 'Produkttabelle';
        case DashboardComponentType.AsljungapallenLagersaldo:
          return 'Åsljungapallen lagersaldo';
        default:
          return 'Unbekannter Dashboard-Komponententyp';
      }
    },
    keyboardAction(action: KeyboardActions): string {
      switch (action) {
        case KeyboardActions.DropOff:
          return 'Abgeben';
        case KeyboardActions.PickUp:
          return 'Abholen';
        case KeyboardActions.ZoomIn:
          return 'Vergrößern';
        case KeyboardActions.ZoomOut:
          return 'Verkleinern';
        case KeyboardActions.ChangeMapStyle:
          return 'Kartenstil ändern';
        case KeyboardActions.OpenInventory:
          return 'Bestand öffnen/schließen';
        case KeyboardActions.RotateLeft:
          return 'Nach links drehen';
        case KeyboardActions.RotateRight:
          return 'Nach rechts drehen';
        case KeyboardActions.ToggleStorageAreaFull:
          return 'Lagerbereich auf „voll“/„in Gebrauch“ umschalten';
        case KeyboardActions.OpenStorageArea:
          return 'Lagerbereich öffnen/schließen';
        case KeyboardActions.Undo:
          return 'Rückgängig';
        case KeyboardActions.CreateStorageArea:
          return 'Lagerbereich anlegen';
        case KeyboardActions.SelectNextNearbyStorageArea:
          return 'Nächstes nahegelegenes Speichergebiet auswählen';
        case KeyboardActions.SelectPreviousNearbyStorageArea:
          return 'Vorheriges nahegelegenes Speichergebiet auswählen';
      }
    },
    productionOrderState(state: ProductionOrderState): string {
      switch (state) {
        case ProductionOrderState.Deleted:
          return 'Gelöscht';
        case ProductionOrderState.Planned:
          return 'Geplant';
        case ProductionOrderState.Released:
          return 'Freigegeben';
        case ProductionOrderState.Reserved:
          return 'Reserviert';
        case ProductionOrderState.Preparing:
          return 'In Vorbereitung';
        case ProductionOrderState.Prepared:
          return 'Vorbereitet';
        case ProductionOrderState.Delivering:
          return 'In Auslieferung';
        case ProductionOrderState.Completed:
          return 'Abgeschlossen';
        case ProductionOrderState.Registered:
          return 'Registriert';
        case ProductionOrderState.PreparingPaused:
          return 'Vorbereitung unterbrochen';
        case ProductionOrderState.DeliveringPaused:
          return 'Auslieferung unterbrochen';
      }

      return 'Unbekannt';
    },
    deliveryStatus(status: DeliveryStatus): string {
      switch (status) {
        case DeliveryStatus.Planned:
          return 'Geplant';
        case DeliveryStatus.Released:
          return 'Freigegeben';
        case DeliveryStatus.Active:
          return 'Aktiv';
        case DeliveryStatus.Finished:
          return 'Abgeschlossen';
        case DeliveryStatus.Invoiced:
          return 'In Rechnung gestellt';
      }

      return 'Unbekannt';
    },
  },
  reports: {
    exportFormat: {
      pdf: 'PDF',
      excel: 'Excel',
    },
    reportName(report: ReportType): string {
      switch (report) {
        case ReportType.LogYardStatus:
          return 'Statusbericht';
        case ReportType.LogYardPeriod:
          return 'Periodenbericht';
        case ReportType.LogYardVehicleMoves:
          return 'Fahrzeugbewegungen';
        case ReportType.LogYardMovedProducts:
          return 'Verschobene Produkte';
        case ReportType.LogYardVehicleStatistics:
          return 'Fahrzeugstatistik';
        default:
          return 'Unbekannter Bericht';
      }
    },
  },
  storageAreas: {
    actions: {
      create: {
        title: 'Lagerbereich anlegen',
        pending: 'Lagerbereich wird angelegt',
        error: 'Lagerbereich konnte nicht angelegt werden',
      },
      fetch: {
        title: 'Lagerbereich wird abgerufen',
        error: 'Lagerbereich konnte nicht abgerufen werden',
      },
      fetchMany: {
        title: 'Lagerbereiche werden abgerufen',
        error: 'Lagerbereiche konnten nicht abgerufen werden',
      },
      inventory: {
        title: 'Bestandsaufnahme des Lagerbereichs',
        error: 'Bestandsaufnahme des Lagerbereichs fehlgeschlagen',
      },
      undoInventory: {
        title: 'Bestandsaufnahme rückgängig machen',
        error: 'Rückgängigmachen der Bestandsaufnahme fehlgeschlagen',
      },
      delete: {
        title: 'Lagerbereich löschen',
        error: 'Löschen des Lagerbereichs fehlgeschlagen',
      },
      update: {
        title: 'Lagerbereich aktualisieren',
        error: 'Aktualisieren des Lagerbereichs fehlgeschlagen',
      },
      undoDelete: {
        title: 'Löschen des Lagerbereichs rückgängig machen',
        error: 'Rückgängigmachen des Löschvorgangs fehlgeschlagen',
      },
    },
    copy: 'Kopieren',
    notFound: 'Lagerbereich nicht gefunden',
    storageAreaTable: {
      firstMoveTo: 'Erste Bewegung',
      daysSinceFirstMove: 'Tage seit der ersten Bewegung',
    },
  },
  productionOrders: {
    actions: {
      fetchMany: {
        title: 'Fertigungsaufträge abrufen',
        error: 'Abrufen der Fertigungsaufträge fehlgeschlagen',
      },
      fetch: {
        title: 'Fertigungsauftrag abrufen',
        error: 'Fertigungsauftrag konnte nicht abgerufen werden',
      },
      create: {
        title: 'Fertigungsauftrag anlegen',
        error: 'Fertigungsauftrag konnte nicht angelegt werden',
      },
      update: {
        title: 'Fertigungsauftrag aktualisieren',
        error: 'Fertigungsauftrag konnte nicht aktualisiert werden',
      },
      delete: {
        title: 'Fertigungsauftrag löschen',
        error: 'Fertigungsauftrag konnte nicht gelöscht werden',
        statusCodes: {
          409: 'Fertigungsauftrag konnte nicht gelöscht werden',
        },
      },
      change: {
        title: 'Fertigungsauftrag ändern',
        error: 'Fertigungsauftrag konnte nicht geändert werden',
      },
    },
    listPage: {
      title: 'Fertigungsaufträge',
      noFound: 'Kein Fertigungsauftrag gefunden',
      loading: 'Fertigungsaufträge werden geladen...',
    },
    deleteDialog: {
      header(name: string) {
        return `Produktionsauftrag ${name} löschen`;
      },
      description:
        'Möchten Sie diesen Fertigungsauftrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Fertigungsauftrag löschen',
    },
    editPage: {
      editTitle: 'Fertigungsauftrag bearbeiten',
      createTitle: 'Fertigungsauftrag anlegen',
      productionInstruction: 'Fertigungsanweisung',
      vehicleInstruction: 'Fahrzeuganweisung',
      state: 'Status',
      recipeInstruction: 'Anweisungen',
      recipeMachineInstruction: 'Maschinenanweisungen',
      consumeEntireStock: 'Gesamten Bestand verbrauchen',
      noProducts: 'Keine Produkte',
      inProductsTitle: 'Eingehende Produkte',
      outProductsTitle: 'Ausgehende Produkte',
      editRecipeTitle: 'Rezept bearbeiten',
      addRecipeTitle: 'Rezept hinzufügen',
      addRecipeButton: 'Rezept hinzufügen',
      recipesTitle: 'Rezepte',
    },
    plannedStart: 'Geplanter Start',
  },
  shapes: {
    name: (id: string) => `Form #${id}`,
    multipleNames: (count: number) => `Bearbeite (${count}) Formen`,
    actions: {
      fetchMany: {
        title: 'Formen abrufen',
        error: 'Formen konnten nicht abgerufen werden',
      },
      fetch: {
        title: 'Form abrufen',
        error: 'Form konnte nicht abgerufen werden',
      },
      update: {
        title: 'Form wird aktualisiert',
        error: 'Form konnte nicht aktualisiert werden',
      },
      create: {
        title: 'Form erstellen',
        pending: 'Form wird erstellt',
        error: 'Form konnte nicht erstellt werden',
      },
      delete: {
        title: 'Form löschen',
        error: 'Form konnte nicht gelöscht werden',
      },
      query: {
        title: 'Formen abfragen',
        error: 'Formen konnten nicht abgefragt werden',
      },
      undoDelete: {
        title: 'Löschen der Form rückgängig machen',
        error: 'Löschen der Form konnte nicht rückgängig gemacht werden',
      },
    },
    geofenceFieldSet: 'Geofence',
    zIndex: 'Z-Index',
  },
  settings: {
    actions: {
      read: {
        title: 'Einstellungen lesen',
        error: 'Einstellungen konnten nicht gelesen werden',
      },
      save: {
        title: 'Einstellungen speichern',
        error: 'Einstellungen konnten nicht gespeichert werden',
      },
    },
    general: {
      title: 'Allgemein',
      language: 'Sprache',
      dark: 'Dunkel',
      light: 'Hell',
      colorMode: 'Farbmodus',
      scale: 'Größe der Benutzeroberfläche',
      auto: 'Automatisch',
      mapColorMode: 'Farbmodus der Karte',
    },
    map: {
      title: 'Karte',
      style: 'Stil',
      coordinate: 'Koordinaten',
      centerMapOnVehicle: 'Karte auf Fahrzeug zentrieren',
      followVehicleHeading: 'Karte mit dem Fahrzeug drehen',
      snapPlacementToPlace: 'Paket beim Ablegen am Abstellort ausrichten',
      showPackagePanel: 'Paket-Panel anzeigen',
    },
    keybinds: {
      title: 'Tastenbelegung',
    },
    about: {
      title: 'Über',
    },
    vehicle: {
      title: 'Fahrzeug',
      activeVehicle: 'Aktives Fahrzeug',
      speedLimit: 'Geschwindigkeitsbegrenzung von 5 km/h zum Auslösen des Abstellbereichs',
    },
  },
  featureFlags: {
    actions: {
      read: {
        title: 'Feature-Flags lesen',
        error: 'Feature-Flags konnten nicht gelesen werden',
      },
      refresh: {
        title: 'Feature-Flags aktualisieren',
        error: 'Feature-Flags konnten nicht aktualisiert werden',
      },
      toggle: {
        title: 'Feature-Flags umschalten',
        error: 'Feature-Flags konnten nicht umgeschaltet werden',
      },
    },
    refresh: {
      success: 'Funktionsflags erfolgreich aktualisiert',
    },
    toggle: {
      success: 'Funktionsflag erfolgreich umgeschaltet',
    },
    component: {
      title: 'Funktionsflags',
      description: 'Funktionen in der Anwendung aktivieren oder deaktivieren',
      refreshButton: 'Funktionsflags aktualisieren',
    },
  },
  auth: {
    loggedOut: {
      title: 'Sie sind abgemeldet!',
      description: 'Sie müssen sich anmelden, um weiterarbeiten zu können.',
    },
    unauthorized: {
      title: 'Ihr Benutzer ist nicht berechtigt, diese App zu nutzen',
      description:
        'Sie müssen sich an Ihren Administrator wenden, damit er Ihnen die Nutzungsberechtigung erteilt.',
    },
  },
  map: {
    contextmenu: {
      create: {
        title: 'Erstellen',
        shape: 'Form',
        storageArea: 'Lagerbereich',
        logSorter: 'Holzsortierer',
        intake: 'Zulauf',
        productionLine: 'Fertigungslinie',
        insideProductionLine: 'Innerhalb der Fertigungslinie erstellen',
        packageOutput: 'Paketausgabe',
        packageIntake: 'Paketannahme',
      },
      edit: 'Bearbeiten',
      delete: 'Löschen',
      audit: 'Prüfen',
      copy: 'Kopieren',
      move: 'Verschieben',
      addInboundDeliveryTransaction: 'Wareneingangstransaktion hinzufügen',
      addOutboundDeliveryTransaction: 'Warenausgangstransaktion hinzufügen',
      spawnPackage: 'Paket erzeugen',
      copyPackage: 'Paket kopieren',
    },
    actions: {
      saveMapMode: {
        title: 'Kartenmodus speichern',
        error: 'Speichern des Kartenmodus fehlgeschlagen',
      },
    },
  },
  productionLine: {
    simulation: 'Simulation',
    areas: 'Bereiche',
    addOutputArea: 'Ausgabebereich hinzufügen',
    addIntakeArea: 'Annahmebereich hinzufügen',
    saveLineFirst:
      'Speichern Sie die Produktionslinie, bevor Sie Ausgabebereiche oder Annahmebereiche hinzufügen.',
    simulationEnabled: 'Simulation aktiviert',
    intervalSeconds: 'Intervall (Sekunden)',
    outputPlace: 'Ausgabestelle',
    product: 'Produkt',
    piecesCount: 'Stückzahl',
    packaging: 'Verpackung',
    packageType: 'Pakettyp',
    length: 'Länge',
    width: 'Breite',
    height: 'Höhe',
  },
  logSorter: {
    actions: {
      query: {
        title: 'Holzsortierer abfragen',
        error: 'Abfrage des Protokollsortierers fehlgeschlagen',
      },
      update: {
        title: 'Stammsortierer wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Stammsortierers',
      },
      create: {
        title: 'Stammsortierer wird erstellt',
        error: 'Fehler beim Erstellen des Stammsortierers',
      },
      delete: {
        title: 'Stammsortierer wird gelöscht',
        error: 'Fehler beim Löschen des Stammsortierers',
      },
      fetch: {
        title: 'Stammsortierer wird abgerufen',
        error: 'Fehler beim Abrufen des Stammsortierers',
      },
    },
    drawer: {
      createTitle: 'Protokollsortierer erstellen',
      binName: (binNumber: number) => `Bin ${binNumber}`,
      logSorterTab: 'Protokollsortierer',
      binsTab: 'Behälter',
    },
    deleteConfirm: {
      title: `Stammsortierer löschen`,
      message: (name: string) =>
        `Sind Sie sicher, dass Sie den Stammsortierer ${name} löschen möchten?`,
    },
    panel: {
      identifier: 'Kennung',
      numberSequence: 'Nummernfolge',
      numberOfBinsCol1: 'Anzahl der Behälter in Spalte eins',
      numberOfBinsCol2: 'Anzahl der Behälter in Spalte zwei',
      binLayout: 'Spalte zwei bis',
      binWidth: 'Behälterbreite',
      binLength: 'Behälterlänge',
      columnGap: 'Spaltenabstand',
      rowGap: 'Zeilenabstand',
      intakes: 'Einläufe',
      offsetY: 'Versatz Y',
      offsetX: 'Versatz X',
    },
  },
  mapStyles: {
    actions: {
      create: {
        title: 'Stil erstellen',
        error: 'Fehler beim Erstellen des Stils',
      },
      update: {
        title: 'Stil aktualisieren',
        error: 'Fehler beim Aktualisieren des Stils',
      },
      delete: {
        title: 'Stil löschen',
        error: 'Fehler beim Löschen des Stils',
      },
      fetch: {
        title: 'Stil abrufen',
        error: 'Fehler beim Abrufen des Stils',
      },
      fetchMany: {
        title: 'Stile abrufen',
        error: 'Fehler beim Abrufen der Stile',
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Stil ${name} löschen`;
      },
      description:
        'Möchten Sie diesen Stil wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Stil löschen',
    },

    editDialog: {
      create: {
        title: 'Neuen Stil erstellen',
      },
      edit: {
        title: 'Stil bearbeiten',
      },
      tip: 'Drücken Sie Strg+Leertaste, um Informationen zu den verfügbaren Funktionen anzuzeigen.',
      documentation: 'Weitere Informationen zu den Formeln finden Sie in der',
      documentationLinkText: 'Dokumentation zu Kartenstilen',
      shapeSet: 'Form',
      vehicleSet: 'Fahrzeug',
      labelFormula: 'Beschriftung',
      colorFormula: 'Farbe',
      outlineColorFormula: 'Konturfarbe',
      outlineSizeFormula: 'Konturgröße',
      fontSizeFormula: 'Schriftgröße',
      messageIconFormula: 'Nachrichtensymbol anzeigen',
      placeSet: 'Lagerplätze',
      placeVisibleFormula: 'Sichtbar',
      placeLabelFormula: 'Beschriftung',
      placeOutlineColorFormula: 'Umrissfarbe',
      placeOutlineSizeFormula: 'Umrissgröße',
      placeAccessibleOutlineColorFormula: 'Umrissfarbe zugängliche Seite',
      placeInaccessibleOutlineColorFormula: 'Umrissfarbe unzugängliche Seite',
      placeTextAlignmentFormula: 'Textausrichtung (left, center, right)',
      placeTextPositionFormula: 'Textposition (center, top, bottom)',
      placeTextOpacityFormula: 'Text-Deckkraft (0-100)',
      placeTextColorFormula: 'Textfarbe',
      placeTextSizeFormula: 'Textgröße',
      placeBackgroundColorFormula: 'Hintergrundfarbe',
      placeBackgroundPatternFormula: 'Hintergrundmuster (solid, hatch, cross, dots)',
      placeBackgroundOpacityFormula: 'Hintergrund-Deckkraft (0-100)',
      packageSet: 'Pakete',
      packageVisibleFormula: 'Sichtbar',
      packageLabelFormula: 'Beschriftung',
      packageOutlineColorFormula: 'Umrissfarbe',
      packageOutlineSizeFormula: 'Umrissgröße',
      packageTextAlignmentFormula: 'Textausrichtung (left, center, right)',
      packageTextPositionFormula: 'Textposition (center, top, bottom)',
      packageTextOpacityFormula: 'Text-Deckkraft (0-100)',
      packageTextColorFormula: 'Textfarbe',
      packageTextSizeFormula: 'Textgröße',
      packageBackgroundColorFormula: 'Hintergrundfarbe',
      packageBackgroundPatternFormula: 'Hintergrundmuster (solid, hatch, cross, dots)',
      packageBackgroundOpacityFormula: 'Hintergrund-Deckkraft (0-100)',
      proximitySet: 'Näherungsbereich',
      proximityVisibleFormula: 'Sichtbar',
      proximityAreaColorFormula: 'Bereichsfarbe',
      proximityConeColorFormula: 'Farbe des Zugriffskegels',
      colorPicker: 'Farbauswahl (dient zur Anzeige und Auswahl von Farbcodes)',
    },

    page: {
      title: 'Kartenstile',
      notFound: 'Keine Kartenstile gefunden',
      loading: 'Kartenstile werden geladen',
      copiedToClipboard: 'In die Zwischenablage kopiert!',
      noStyleInClipboard: 'Kein Stil in der Zwischenablage',
      noStyleInClipboardDescription:
        'Sie müssen einen Stil in die Zwischenablage exportieren, bevor Sie ihn importieren können.',
      importFromClipboardTooltip: 'Aus der Zwischenablage importieren',
    failedToImport: 'Import aus der Zwischenablage fehlgeschlagen:',
    clipBoardAccessDeniedSummary: 'Zugriff auf die Zwischenablage verweigert',
    clipBoardAccessDeniedDetail: 'Erlauben Sie dieser Website den Zugriff auf die Zwischenablage und versuchen Sie es erneut.'
    },
  },
  systemSettings: {
    page: {
      title: 'Systemeinstellungen',
      settingsSaved: 'Einstellungen erfolgreich gespeichert',
      storagearea: {
        title: 'Speicherbereich',
        nameFormat: 'Namensformat',
        nameFormatRequired: 'Namensformat ist erforderlich',
        nameFormatMissingSequence:
          'Das Namensformat muss den Platzhalter {#} für die Sequenz enthalten',
        displayText: 'Anzeigetext',
        displayTextFormula: 'Formel für Anzeigetext',
        displayTextFormulaTitle: 'Anzeigetext konfigurieren',
        displayTextFormulaDescription:
          'Diese Formel wird verwendet, um den Anzeigetext jedes Lagerbereichs zu berechnen. Sie verwendet dieselben Variablen und Funktionen wie die Formeln für die Formen im Kartenstil.',
        configureDisplayText: 'Formel für Anzeigetext konfigurieren',
        placeDisplayText: 'Anzeigetext für Lagerplatz',
        placeDisplayTextFormula: 'Formel für Anzeigetext für Lagerplatz',
        placeDisplayTextFormulaTitle: 'Anzeigetext für Lagerplatz konfigurieren',
        placeDisplayTextFormulaDescription:
          'Diese Formel wird verwendet, um den Anzeigetext jedes Lagerplatzes zu berechnen. Sie verwendet dieselben Variablen und Funktionen wie die Formeln für die Lagerplätze im Kartenstil.',
        configurePlaceDisplayText: 'Formel für den Platz-Anzeigetext konfigurieren',
        logYardLength: 'Länge des Holzlagers',
        logYardWidth: 'Breite des Holzlagers',
        logYardSideArea: 'Fläche der Holzlager-Seiten',
        logYard: 'Holzlager',
      },
      featureflags: {
        title: 'Funktionsflags',
      },
      reports: {
        title: 'Berichte',
        defaultReports: 'Standardberichte',
        customerSpecificReports: 'Nur für bestimmte Kunden',
      },
      map: {
        minZoom: 'Mindestzoomstufe der Karte (0–24)',
      },
      dashboards: {
        title: 'Dashboard-Komponenten',
        defaultComponents: 'Standardkomponenten',
        customerSpecificComponents: 'Nur für bestimmte Kunden',
      },
    },
  },
  actions: {
    fetchVersion: {
      title: 'Anwendungsversion abrufen',
      error: 'Anwendungsversion konnte nicht abgerufen werden',
    },
  },
  undo: {
    undoBtn: 'Rückgängig',
    pickTitle: 'Auswahl rückgängig machen',
    getUndoPickDescription(
      oldStorageAreaCount: number,
      oldVehicleCount: number,
      storageAreaName: string,
    ): string {
      return `Confirm to remove ${oldVehicleCount} logs from your vehicle and restore storage area ${storageAreaName} to amount: ${oldStorageAreaCount}`;
    },
    getUndoPickBulkDescription(products: string, storageAreaName: string): string {
      return `Restore ${products} to ${storageAreaName} and remove it from your vehicle.`;
    },
    placeTitle: 'Platzierung rückgängig machen',
    getUndoPlaceDescription(numberOfLogs: number, storageAreaName: string): string {
      return `Bestätigen Sie, um ${numberOfLogs} Stämme zu Ihrem Fahrzeug zurückzugeben und ${numberOfLogs} Stämme aus dem Lagerbereich ${storageAreaName} zu entfernen`;
    },
    getUndoPlaceBulkDescription(products: string, storageAreaName: string): string {
      return `Einlagerung von ${products} in ${storageAreaName} rückgängig machen. Das Schüttgut wird aus dem Lagerbereich entfernt und auf dein Fahrzeug zurückgebucht.`;
    },
    inventoryTitle: 'Bestandsaktion rückgängig machen',
    getUndoInventoryDescription(
      productname: string,
      numberOfLogs: number,
      storageAreaName: string,
    ): string {
      return `Bestätigen Sie, um die letzte Inventaraktion im Lagerbereich ${storageAreaName} rückgängig zu machen und die Menge von ${productname} auf ${numberOfLogs} zurückzusetzen`;
    },
    getUndoBulkInventoryDescription(
      productname: string,
      value: number,
      unit: Unit,
      storageAreaName: string,
    ): string {
      return `Bestätigen Sie, um die letzte inventuraktion im lagerbereich rückgängig zu machen ${storageAreaName} und festgelegten betrag ${productname} zurück zu ${value}`;
    },
    createStorageAreaTitle: 'Lagerbereich anlegen rückgängig machen',
    getUndoCreateStorageAreaDescription(storageAreaName: string): string {
      return `Bestätigen Sie, um die Erstellung des Lagerbereichs ${storageAreaName} rückgängig zu machen`;
    },
    deleteStorageAreaTitle: 'Lagerbereich löschen rückgängig machen',
    getUndoDeleteStorageAreaDescription(storageAreaName: string): string {
      return `Bestätigen Sie, um die Löschung des Lagerbereichs ${storageAreaName} rückgängig zu machen`;
    },
    editStorageAreaTitle: 'Lagerbereich bearbeiten rückgängig machen',
    getUndoEditStorageAreaDescription(storageAreaName: string): string {
      return `Bestätigen Sie, um die Bearbeitung des Lagerbereichs ${storageAreaName} rückgängig zu machen`;
    },
    createShape: {
      title: 'Form-Erstellung rückgängig machen',
      description: `Bestätigen Sie, um die Form-Erstellung rückgängig zu machen`,
    },
    updateShape: {
      title: 'Form aktualisieren rückgängig machen',
      description: 'Bestätigen, um die Aktualisierung der Form rückgängig zu machen',
    },
    deleteShape: {
      title: 'Form löschen rückgängig machen',
      description: 'Bestätigen, um das Löschen der Form rückgängig zu machen',
    },
  },
  quickMenu: {
    addStorageArea: 'Lagerbereich anlegen',
    editStorageArea: 'Lagerbereich bearbeiten',
  },
  moves: {
    actions: {
      moveTimberFromLogSorterBins: {
        title: 'Holz aus den Behältern des Rundholzsortierers bewegen',
        error: 'Holz konnte nicht aus den Behältern des Rundholzsortierers bewegt werden',
      },
      pick: {
        title: 'Entnahme aus dem Lagerbereich',
        error: 'Entnahme aus dem Lagerbereich fehlgeschlagen',
      },
      place: {
        title: 'Im Lagerbereich ablegen',
        error: 'Das Ablegen aus dem Lagerbereich ist fehlgeschlagen',
      },
      pickAndPlace: {
        title: 'Entnehmen und ablegen',
        error: 'Das Entnehmen und Ablegen ist fehlgeschlagen',
      },
    },
    failedDialog: {
      title: 'Verschieben fehlgeschlagen!',
      description: (fromStorageArea: string, toStorageArea: string) =>
        `Verschiebung von ${fromStorageArea} nach ${toStorageArea}`,
    },
    moveTimberFromLogSorterBins: 'Holz aus den Behältern bewegen',
  },
  home: {
    welcome: 'Willkommen bei',
    description: 'dem Logistiksystem der nächsten Generation',
    username: 'Benutzername',
    enterUsername: 'Geben Sie Ihren Benutzernamen ein, um sich anzumelden',
    rememberUsername: 'Benutzernamen speichern',
    signInButton: 'Anmelden',
  },
  unsavedChanges: {
    title: 'Sie haben ungespeicherte Änderungen!',
    description: 'Was möchten Sie damit tun?',
  },
  audit: {
    dialog: {
      title: (name: string) => `Änderungen von ${name}`,
    },
    actions: {
      fetchShape: {
        title: 'Formprüfung abrufen',
        error: 'Das Abrufen der Formprüfung ist fehlgeschlagen',
      },
      fetchVehicle: {
        title: 'Fahrzeugprüfung abrufen',
        error: 'Das Abrufen der Fahrzeugprüfung ist fehlgeschlagen',
      },
      fetchProduct: {
        title: 'Produktprüfung abrufen',
        error: 'Produktprüfung konnte nicht abgerufen werden',
      },
      fetchPackage: {
        title: 'Paketprüfung abrufen',
        error: 'Paketprüfung konnte nicht abgerufen werden',
      },
      fetchPackaging: {
        title: 'Verpackungsprüfung abrufen',
        error: 'Verpackungsprüfung konnte nicht abgerufen werden',
      },
      fetchPackageType: {
        title: 'Pakettypprüfung abrufen',
        error: 'Pakettypprüfung konnte nicht abgerufen werden',
      },
      fetchProductGroup: {
        title: 'Produktgruppenprüfung abrufen',
        error: 'Produktgruppenprüfung konnte nicht abgerufen werden',
      },
      fetchDashboard: {
        title: 'Dashboard-Prüfung abrufen',
        error: 'Dashboard-Prüfung konnte nicht abgerufen werden',
      },
      fetchProductionOrder: {
        title: 'Fertigungsauftragsprüfung abrufen',
        error: 'Fertigungsauftragsprüfung konnte nicht abgerufen werden',
      },
      fetchInboundDelivery: {
        title: 'Anlieferungsprüfung abrufen',
        error: 'Anlieferungsprüfung konnte nicht abgerufen werden',
      },
      fetchOutboundDelivery: {
        title: 'Auslieferungsprüfung abrufen',
        error: 'Auslieferungsprüfung konnte nicht abgerufen werden',
      },
    },
  },
  packages: {
    actions: {
      fetchMany: {
        title: 'Pakete werden abgerufen',
        error: 'Fehler beim Abrufen der Pakete',
      },
      create: {
        title: 'Paket wird erstellt',
        error: 'Fehler beim Erstellen des Pakets',
      },
      update: {
        title: 'Paket wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Pakets',
      },
      delete: {
        title: 'Paket wird gelöscht',
        error: 'Fehler beim Löschen des Pakets',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Paket ${name} löschen`,
      description: 'Sind Sie sicher, dass Sie dieses Paket löschen möchten?',
      deleteButton: 'Ja, Paket löschen',
    },
    packages: 'Pakete',
    addNewPackage: 'Paket hinzufügen',
    editPackage: 'Paket bearbeiten',
    groups: {
      core: 'Allgemein',
      physical: 'Physisch',
      nominal: 'Nominal',
      statistics: 'Statistiken',
    },
    layoutMode: 'Layout',
    layoutModes: {
      fieldset: 'Feldgruppen',
      accordion: 'Akkordeon',
    },
    showDialogOptions: 'Dialogoptionen anzeigen',
    hideDialogOptions: 'Dialogoptionen ausblenden',
    weightExceedsMaxWarning: 'Das Gesamtgewicht überschreitet das maximal zulässige Gewicht für einen oder mehrere belegte Lagerplätze.',
    heightExceedsMaxWarning: 'Die Gesamthöhe überschreitet die maximal zulässige Höhe für einen oder mehrere belegte Lagerplätze.',
    stackLevelExceedsMaxWarning: 'Die Stapelebene überschreitet die maximale Stapelanzahl für einen oder mehrere belegte Lagerplätze.',
    placementOverlapWarning: 'Die gewählten Lagerplätze sind auf dieser Stapelebene bereits durch ein anderes Paket belegt.',
    placesNotContiguousWarning: 'Die belegten Lagerplätze sind nicht zusammenhängend.',
    occupiedPlacesNotContiguousWarning: 'Die belegten Lagerplätze sind nicht zusammenhängend.',
    moveDialog: 'Dialog verschieben',
    dragGroup: 'Ziehen',
    moveGroupUp: 'Gruppe nach oben verschieben',
    moveGroupDown: 'Gruppe nach unten verschieben',
    resizeGroupHeight: 'Gruppenhoehe anpassen',
    resizeGroupWidth: 'Gruppenbreite anpassen',
    fields: {
      number: 'Nummer',
      externalNumber: 'Externe Nummer',
      packageStorageArea: 'Paketlagerbereich',
      anchorStoragePlace: 'Anker-Lagerplatz',
      occupiedStoragePlaces: 'Belegte Lagerplätze',
      stackLevel: 'Stapelhöhe',
      accessibilityRating: 'Zugänglichkeitsbewertung',
      nominalWeight: 'Nominalgewicht',
      weight: 'Gewicht',
      nominalHeight: 'Nominalhöhe',
      height: 'Höhe',
      nominalWidth: 'Nominalbreite',
      width: 'Breite',
      nominalLength: 'Nominallänge',
      length: 'Länge',
      nominalVolume: 'Nominalvolumen',
      volume: 'Volumen',
      nominalRunningMeters: 'Nominal-Laufmeter',
      runningMeters: 'Laufmeter',
      statusValidFrom: 'Status gültig von',
      statusValidTo: 'Status gültig bis',
      comment: 'Kommentar',
      timesMovedCount: 'Anzahl Bewegungen',
      distanceTravelledKm: 'Zurückgelegte Strecke',
      timeCarried: 'Transportzeit',
      weightedDistanceTravelledKm: 'Gewichtete Fahrstrecke',
      createdAt: 'Erstellt',
      piecesCount: 'Stück',
      layers: 'Lagen',
      piecesPerLayer: 'Stück pro Lage',
      spacerHeight: 'Stapellattenhöhe',
      composition: 'Zusammensetzung',
    },
    compositions: {
      Empty: 'Leer',
      Uniform: 'Einheitlich',
      Mixed: 'Gemischt',
    },
    panel: {
      pickupTitle: 'Aufnehmen',
      placementTitle: 'Ablegen',
      noNearbyPackages: 'Keine Pakete in der Nähe',
      noNearbyPlaces: 'Keine Lagerplätze in der Nähe',
      number: 'Nummer',
      accessibilityRating: 'Zugänglichkeit',
      distance: 'Entfernung',
      accessible: 'Zugänglich',
      access: 'Zugang',
      reason: 'Grund',
      yes: 'Ja',
      no: 'Nein',
      front: 'Vorne',
      right: 'Rechts',
      back: 'Hinten',
      left: 'Links',
      pickup: 'Aufnehmen',
      putdown: 'Ablegen',
      pickupLimitReached: 'Aufnahmelimit erreicht',
      carriedPackages: 'Getragene Pakete',
      nothingCarried: 'Keine Pakete geladen',
      occupiesPlaces: (count: number) => `Belegt ${count} Platz/Plätze`,
      doesNotFit: 'Paket passt nicht in die verfügbaren Plätze',
      notInAccessDirection: 'Paket ist nicht in Zugriffsrichtung',
      pickupBlockedReasonPlaceNotAccessible:
        'Der ausgewählte Lagerplatz erlaubt die Aufnahme von keiner Seite',
      pickupBlockedReasonWrongAccessSide: 'Nähere dich dem Paket von einer zugänglichen Seite',
      pickupBlockedReasonPackageOrientationMismatch:
        'Die Paket-Aufnahmerichtung des Fahrzeugs passt nicht zur Paketausrichtung',
      approachAccessibleSide: 'Nähern Sie sich dem Platz von einer zugänglichen Seite',
    },
    details: {
      title: 'Paketdetails',
      summary: 'Übersicht',
      dimensions: 'Volumen & Abmessungen',
      contents: 'Inhalt',
      noContents: 'Kein Inhalt',
      status: 'Status',
      packageType: 'Pakettyp',
      storageArea: 'Lagerbereich',
      quality: 'Qualität',
      age: 'Alter',
      ageDays: (days: number) => `${days} Tag(e)`,
      product: 'Produkt',
      productCode: 'Produktcode',
      productGroup: 'Produktgruppe',
      pieces: 'Stückzahl',
      thickness: 'Dicke',
      nominalThickness: 'Nenndicke',
      moisture: 'Feuchte',
      partNumber: 'Teilenummer',
      codes: 'Codes',
      resize: 'Größe ändern',
    },
    contentTable: {
      mappingTrace: 'Zuordnungsverlauf',
      hints: {
        physical: 'Tatsächlich gemessene Werte der Inhaltszeile. Gewicht in kg (gesamte Zeile), Dicke, Breite und Länge in mm. Volumen (m³) und Laufmeter (m) werden aus Stückzahl und Abmessungen berechnet.',
        nominal: 'Vorgegebene Sollwerte der Inhaltszeile. Gewicht in kg (gesamte Zeile), Dicke, Breite und Länge in mm. Volumen (m³) und Laufmeter (m) werden aus Stückzahl und Abmessungen berechnet.',
        moisture: 'Feuchtegehalt in %.',
        quality: 'Qualitätsklasse des Inhalts. Standardmäßig vom Produkt übernommen.',
        partNumber: 'Kennung dieses Teils innerhalb des Pakets.',
        mappingTrace: 'Produktzuordnung, aus der dieser Inhalt entstanden ist.',
        codes: 'Identifikationscodes, z. B. Barcodes, dieses Inhalts. Zum Bearbeiten klicken.',
      },
      inputs: {
        weight: 'Gewicht (kg)',
        thickness: 'Dicke (mm)',
        width: 'Breite (mm)',
        length: 'Länge (mm)',
        volume: 'Volumen (m³), berechnet wenn Stückzahl, Dicke, Breite und Länge gesetzt sind',
        runningMeters: 'Laufmeter (m), berechnet wenn Stückzahl und Länge gesetzt sind',
      },
    },
  },
  packaging: {
    actions: {
      fetchMany: {
        title: 'Verpackungen werden abgerufen',
        error: 'Fehler beim Abrufen der Verpackungen',
      },
      create: {
        title: 'Verpackung wird erstellt',
        error: 'Fehler beim Erstellen der Verpackung',
      },
      update: {
        title: 'Verpackung wird aktualisiert',
        error: 'Fehler beim Aktualisieren der Verpackung',
      },
      delete: {
        title: 'Verpackung wird gelöscht',
        error: 'Fehler beim Löschen der Verpackung',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Verpackung ${name} löschen`,
      description: 'Sind Sie sicher, dass Sie diese Verpackung löschen möchten?',
      deleteButton: 'Ja, Verpackung löschen',
    },
    packaging: 'Verpackung',
    addNewPackaging: 'Verpackung hinzufügen',
    editPackaging: 'Verpackung bearbeiten',
  },
  packageTypes: {
    actions: {
      fetchMany: {
        title: 'Pakettypen werden abgerufen',
        error: 'Fehler beim Abrufen der Pakettypen',
      },
      create: {
        title: 'Pakettyp wird erstellt',
        error: 'Fehler beim Erstellen des Pakettyps',
      },
      update: {
        title: 'Pakettyp wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Pakettyps',
      },
      delete: {
        title: 'Pakettyp wird gelöscht',
        error: 'Fehler beim Löschen des Pakettyps',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Pakettyp ${name} löschen`,
      description: 'Sind Sie sicher, dass Sie diesen Pakettyp löschen möchten?',
      deleteButton: 'Ja, Pakettyp löschen',
    },
    packageTypes: 'Pakettypen',
    addNewPackageType: 'Pakettyp hinzufügen',
    editPackageType: 'Pakettyp bearbeiten',
  },
  units: {
    millimeters: 'mm',
    centimeters: 'cm',
    meters: 'm',
    cubicMeter: 'm³',
    kilogram: 'kg',
    ton: 'Tonne',
    percent: '%',
    kgPerM3: 'kg/m³',
  },
  coils: {
    actions: {
      fetchMany: {
        title: 'Spulen abrufen',
        error: 'Spulen konnten nicht abgerufen werden',
      },
      fetch: {
        title: 'Spule abrufen',
        error: 'Spule konnte nicht abgerufen werden',
      },
      create: {
        title: 'Spule erstellen',
        error: 'Spule konnte nicht erstellt werden',
      },
      update: {
        title: 'Spule aktualisieren',
        error: 'Spule konnte nicht aktualisiert werden',
      },
      delete: {
        title: 'Spule löschen',
        error: 'Spule konnte nicht gelöscht werden',
      },
      undoMove: {
        title: 'Spulenverschiebung rückgängig machen',
        error: 'Spulenverschiebung konnte nicht rückgängig gemacht werden',
      },
    },

    page: {
      title: 'Spulen',
      notFound: 'Keine Spulen gefunden',
      loading: 'Spulen werden geladen',
    },

    deleteDialog: {
      header(name: string) {
        return `Delete coil ${name}`;
      },
      description:
        'Möchten Sie diese Spule wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Spule löschen',
    },

    editDialog: {
      create: {
        title: 'Neue Spule erstellen',
      },
      edit: {
        title: 'Spule bearbeiten',
      },
    },

    slots: 'Anzahl der Steckplätze',
    slotWidth: 'Steckplatzbreite',
    maxHeight: 'Max. Höhe',
    maxLevels: 'Max. Ebenen',
    diameter: 'Durchmesser',
    depth: 'Tiefe',
    product: 'Produkt',
    storageArea: 'Lagerbereich',
    position: 'Position',
    level: 'Ebene',
    maxCoilDiameter: 'Max. Spulendurchmesser',

    moveDialog: {
      title: 'Spule verschieben',
    },

    unableToDrawCoils: (coils: string[]) => {
      if (coils.length === 1) {
        return `${coils[0]} is on a invalid position inside the storage area`;
      } else {
        return 'Viele Spulen haben ungültige Positionen innerhalb des Lagerbereichs';
      }
    },
  },
  logYard: {
    moveDialog: {
      title: 'Stämme verschieben',
    },
    logs: 'Stämme',
  },
  bulk: {
    actions: {
      fetch: {
        title: 'Massenobjekt abrufen',
        error: 'Das Abrufen des Massenobjekts ist fehlgeschlagen',
      },
      create: {
        title: 'Massenobjekt erstellen',
        error: 'Das Erstellen des Massenobjekts ist fehlgeschlagen',
      },
      update: {
        title: 'Massenobjekt aktualisieren',
        error: 'Die Aktualisierung des Massenobjekts ist fehlgeschlagen',
      },
      delete: {
        title: 'Massenobjekt löschen',
        error: 'Das Löschen des Massenobjekts ist fehlgeschlagen',
      },
      undoMove: {
        title: 'Massenobjektverschiebung rückgängig machen',
        error: 'Das Rückgängigmachen der Massenobjektverschiebung ist fehlgeschlagen',
      },
    },
    maxWeight: 'Maximales Gewicht',
    maxVolume: 'Maximales Volumen',

    bulkUnit(value?: Unit | null): string {
      switch (value) {
        case Unit.Kilogram:
          return 'Kilogramm';
        case Unit.CubicMeter:
          return 'Kubikmeter';
        case Unit.LongTon:
          return 'Long Tons';
        case Unit.MetricTon:
          return 'Tonnen';
        case Unit.Pound:
          return 'Pfund';
        case Unit.ShortTon:
          return 'Short Tons';
        default:
          return '';
      }
    },
    bulkUnitShort(value?: Unit | null): string {
      switch (value) {
        case Unit.MetricTon:
          return 't';
        case Unit.Kilogram:
          return 'kg';
        case Unit.CubicMeter:
          return 'm³';
        case Unit.LongTon:
          return 'l.tn';
        case Unit.Pound:
          return 'lbs';
        case Unit.ShortTon:
          return 'tn';
        default:
          return '';
      }
    },
    moveDialog: {
      title: 'Massenverschiebung',
    },
  },
  dashboard: {
    actions: {
      fetchMany: {
        title: 'Dashboards abrufen',
        error: 'Dashboards konnten nicht abgerufen werden',
      },
      fetch: {
        title: 'Dashboard abrufen',
        error: 'Dashboard konnte nicht abgerufen werden',
      },
      create: {
        title: 'Dashboard erstellen',
        error: 'Dashboard konnte nicht erstellt werden',
      },
      update: {
        title: 'Dashboard aktualisieren',
        error: 'Dashboard konnte nicht aktualisiert werden',
      },
      delete: {
        title: 'Dashboard löschen',
        error: 'Dashboard konnte nicht gelöscht werden',
        statusCodes: {
          409: 'Dashboard konnte nicht gelöscht werden',
        },
      },
    },
    listPage: {
      title: 'Dashboards',
      noFound: 'Kein Dashboard gefunden',
      loading: 'Dashboards werden geladen...',
    },
    deleteDialog: {
      header(name: string) {
        return `Dashboard ${name} löschen`;
      },
      description:
        'Möchten Sie dieses Dashboard wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Dashboard löschen',
    },
    editPage: {
      editTitle: 'Dashboard bearbeiten',
      createTitle: 'Dashboard erstellen',
    },
    editComponentDialog: {
      editTitle: 'Komponente bearbeiten',
      addTitle: 'Komponente hinzufügen',
    },
    productGroupTable: {
      title: 'Bestand nach Produktgruppe',
    },
    productTable: {
      title: 'Bestand nach Produkt',
    },
    productGroupDiagram: {
      title: 'Bestand nach Produktgruppe',
    },
    noDashboards: 'Keine Dashboards verfügbar',
  },
  overview: {
    title: 'Übersicht',
  },
  releaseNotes: {
    actions: {
      fetchLatest: {
        title: 'Neueste Versionshinweise abrufen',
        error: 'Abruf der neuesten Versionshinweise fehlgeschlagen',
      },
      fetchMany: {
        title: 'Versionshinweise abrufen',
        error: 'Abruf der Versionshinweise fehlgeschlagen',
      },
    },
  },
  keybinds: {
    action: 'Aktion',
    keybind: 'Tastenkombination',
  },
  reportPage: {
    selectReport: 'Bericht auswählen',
    showReport: 'Bericht anzeigen',
    selectExportFormat: 'Exportformat auswählen',
    exportReport: 'Bericht exportieren',
    exportError: 'Bericht konnte nicht exportiert werden',
    showError: 'Bericht konnte nicht angezeigt werden',
  },
  mapTable: {
    col: {
      name: 'Name',
      type: 'Typ',
    },
  },
  dataTable: {
    filterBy: 'Filtern nach',
    clearFilters: 'Filter löschen',
    showing: 'Anzeige',
    of: 'von',
    filtered: 'gefiltert',
  },
  primeVueLocale: {
    startsWith: 'Beginnt mit',
    contains: 'Enthält',
    notContains: 'Enthält nicht',
    endsWith: 'Endet mit',
    equals: 'Ist gleich',
    notEquals: 'Ist nicht gleich',
    noFilter: 'Kein Filter',

    lt: 'Kleiner als',
    lte: 'Kleiner oder gleich',
    gt: 'Größer als',
    gte: 'Größer oder gleich',

    dateIs: 'Datum ist',
    dateIsNot: 'Datum ist nicht',
    dateBefore: 'Datum ist vor',
    dateAfter: 'Datum ist nach',

    clear: 'Löschen',
    apply: 'Übernehmen',

    matchAll: 'Alle treffen',
    matchAny: 'Beliebige treffen',
    addRule: 'Regel hinzufügen',
    removeRule: 'Regel entfernen',

    accept: 'Akzeptieren',
    reject: 'Ablehnen',

    choose: 'Auswählen',
    upload: 'Hochladen',
    cancel: 'Abbrechen',
    completed: 'Abgeschlossen',
    pending: 'Ausstehend',

    fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB'],

    dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
    dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],

    monthNames: [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ],

    chooseYear: 'Jahr auswählen',
    chooseMonth: 'Monat auswählen',
    chooseDate: 'Datum auswählen',

    prevDecade: 'Vorheriges Jahrzehnt',
    nextDecade: 'Nächstes Jahrzehnt',
    prevYear: 'Vorheriges Jahr',
    nextYear: 'Nächstes Jahr',
    prevMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    prevHour: 'Vorherige Stunde',
    nextHour: 'Nächste Stunde',
    prevMinute: 'Vorherige Minute',
    nextMinute: 'Nächste Minute',
    prevSecond: 'Vorherige Sekunde',
    nextSecond: 'Nächste Sekunde',

    am: 'AM',
    pm: 'PM',
    today: 'Heute',
    weekHeader: 'KW',

    firstDayOfWeek: 1,
    showMonthAfterYear: true,
    dateFormat: 'dd.mm.yy',

    weak: 'Schwach',
    medium: 'Mittel',
    strong: 'Stark',
    passwordPrompt: 'Passwort eingeben',

    emptyFilterMessage: 'Keine Ergebnisse gefunden',
    searchMessage: '{0} Ergebnisse verfügbar',
    selectionMessage: '{0} Elemente ausgewählt',
    emptySelectionMessage: 'Keine Elemente ausgewählt',
    emptySearchMessage: 'Keine Ergebnisse gefunden',
    emptyMessage: 'Keine Daten verfügbar',

    fileChosenMessage: '{0} Dateien',
    noFileChosenMessage: 'Keine Datei ausgewählt',
  },
  delivery: {
    plannedStart: 'Geplante Startzeit',
    plannedFinish: 'Geplante Endzeit',
    actualStart: 'Tatsächliche Startzeit',
    actualFinish: 'Tatsächliche Endzeit',
  },
  inboundDelivery: {
    actions: {
      fetchMany: {
        title: 'Eingehende Lieferungen werden abgerufen',
        error: 'Das Abrufen der eingehenden Lieferungen ist fehlgeschlagen',
      },
      fetch: {
        title: 'Eingehende Lieferungen werden abgerufen',
        error: 'Das Abrufen der eingehenden Lieferungen ist fehlgeschlagen',
      },
      create: {
        title: 'Eingehende Lieferungen werden erstellt',
        error: 'Das Erstellen der eingehenden Lieferungen ist fehlgeschlagen',
      },
      update: {
        title: 'Eingehende Lieferungen werden aktualisiert',
        error: 'Das Aktualisieren der eingehenden Lieferungen ist fehlgeschlagen',
      },
      delete: {
        title: 'Eingehende Lieferungen werden gelöscht',
        error: 'Das Löschen der eingehenden Lieferungen ist fehlgeschlagen',
        statusCodes: {
          409: 'Das Löschen der eingehenden Lieferungen ist fehlgeschlagen',
        },
      },
      transaction: {
        title: 'Eingehende Liefertransaktion wird erstellt',
        error: 'Das Erstellen der eingehenden Liefertransaktion ist fehlgeschlagen',
      },
      fetchTransactions: {
        title: 'Transaktionen für die Lieferung abrufen',
        error: 'Transaktionen für die Lieferung konnten nicht abgerufen werden',
      },
    },
    listPage: {
      title: 'Eingehende Lieferungen',
      noFound: 'Keine Lieferung gefunden',
      loading: 'Lieferungen werden geladen...',
      transactions: 'Transaktionen anzeigen',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete delivery ${name}`;
      },
      description: 'Möchten Sie diese Lieferung wirklich löschen?',
      deleteButton: 'Ja, Lieferung löschen',
    },
    editPage: {
      editTitle: 'Anlieferung bearbeiten',
      createTitle: 'Anlieferung anlegen',
      rowsTitle: 'Zeilen',
      addRow: 'Zeile hinzufügen',
      noRows: 'Keine Zeilen hinzugefügt',
      productRequired: 'Für alle Anlieferungszeilen ist ein Produkt erforderlich.',
      rowWeight: 'Gewicht',
      rowVolume: 'Volumen',
      rowDeliveredWeight: 'Liefergewicht',
      rowDeliveredVolume: 'Liefervolumen',
    },
    transactionDialog: {
      openButton: 'Ankommende Transaktion hinzufügen',
      title: 'Ankommende Transaktion hinzufügen',
      filterMode: 'Lieferungsfilter',
      filterMatchingStorageArea: 'Passender Lagerbereich',
      filterAllDeliveries: 'Alle Lieferungen',
      delivery: 'Lieferung',
      deliveryRow: 'Lieferungszeile',
      selectDelivery: 'Lieferung auswählen',
      selectDeliveryRow: 'Lieferungszeile auswählen',
      selectStorageArea: 'Lagerbereich auswählen',
      deliveryRequired: 'Lieferung ist erforderlich.',
      deliveryRowRequired: 'Lieferungszeile ist erforderlich.',
      storageAreaRequired: 'Lagerbereich ist erforderlich.',
      weightRequired: 'Gewicht ist erforderlich.',
    },
    transactionListDialog: {
      title: 'Transaktionen für die Lieferung',
      noFound: 'Keine Transaktionen gefunden',
      loading: 'Transaktionen werden abgerufen',
      transactionTime: 'Zeitpunkt',
    },
  },
  outboundDelivery: {
    actions: {
      fetchMany: {
        title: 'Abrufen ausgehender  Lieferungen',
        error: 'Abruf der ausgehender Lieferungen fehlgeschlagen',
      },
      fetch: {
        title: 'Abrufen ausgehender Lieferungen',
        error: 'Abruf der ausgehender Lieferungen fehlgeschlagen',
      },
      create: {
        title: 'Anlegen von Warenausgangslieferungen',
        error: 'Fehler beim Erstellen von Auslieferungen',
      },
      update: {
        title: 'Aktualisierung von Warenausgangslieferungen',
        error: 'Aktualisierung der ausgehenden Lieferungen fehlgeschlagen',
      },
      delete: {
        title: 'Löschen von Auslieferungen',
        error: 'Löschen der Auslieferungen fehlgeschlagen',
        statusCodes: {
          409: 'Löschen der Auslieferungen fehlgeschlagen',
        },
      },
      transaction: {
        title: 'Erstellen einer Warenausgangstransaktion',
        error: 'Fehler beim Erstellen der Warenausgangstransaktion',
      },
      fetchTransactions: {
        title: 'Transaktionen für die Lieferung abrufen',
        error: 'Transaktionen für die Lieferung konnten nicht abgerufen werden',
      },
    },
    listPage: {
      title: 'Ausgehende Lieferungen',
      noFound: 'Keine Lieferung gefunden',
      loading: 'Lieferungen werden geladen...',
      transactions: 'Transaktionen anzeigen',
    },
    deleteDialog: {
      header(name: string) {
        return `Lieferung löschen ${name}`;
      },
      description: 'Sind Sie sicher, dass Sie diese Lieferung löschen möchten?',
      deleteButton: 'Ja, Lieferung löschen',
    },
    editPage: {
      editTitle: 'Auslieferung bearbeiten',
      createTitle: 'Auslieferung anlegen',
      rowsTitle: 'Zeilen',
      addRow: 'Zeile hinzufügen',
      noRows: 'Keine Zeilen hinzugefügt',
      productRequired: 'Produkt ist für alle Warenausgangszeilen erforderlich.',
      rowWeight: 'Gewicht',
      rowVolume: 'Volumen',
      rowDeliveredWeight: 'Versandtes Gewicht',
      rowDeliveredVolume: 'Versandtes Volumen',
    },
    transactionDialog: {
      openButton: 'Warenausgangstransaktion hinzufügen',
      title: 'Warenausgangstransaktion hinzufügen',
      filterMode: 'Lieferfilter',
      filterMatchingStorageArea: 'Passender Lagerbereich',
      filterAllDeliveries: 'Alle Lieferungen',
      delivery: 'Lieferung',
      deliveryRow: 'Lieferzeile',
      selectDelivery: 'Lieferung auswählen',
      selectDeliveryRow: 'Lieferzeile auswählen',
      selectStorageArea: 'Lagerbereich auswählen',
      deliveryRequired: 'Lieferung ist erforderlich.',
      deliveryRowRequired: 'Lieferzeile ist erforderlich.',
      storageAreaRequired: 'Lagerbereich ist erforderlich.',
      weightRequired: 'Gewicht ist erforderlich.',
    },
    transactionListDialog: {
      title: 'Transaktionen für die Lieferung',
      noFound: 'Keine Transaktionen gefunden',
      loading: 'Transaktionen werden abgerufen',
      transactionTime: 'Zeitpunkt',
    },
  },
};
